"use client";

import { useState, useEffect, CSSProperties } from "react";
import Image from "next/image";
import { Video, Armchair, Clock, CheckCircle, Loader2 } from "lucide-react";
import {
  useSenOTPMutation,
  useVerifyOTPMutation,
} from "@/redux/features/optVerification/optVerification";
import { ServiceTheme } from "./BookingStepper";
import { getBusinessTimezoneDisplay } from "@/lib/timezone";

// Brand colors matching BookingStepper theme colors
const themePrimaryColors: Record<ServiceTheme, { primary: string; hover: string }> = {
  counseling: { primary: "#896deb", hover: "#7a5de0" },
  lifeCoaching: { primary: "#DDA500", hover: "#c99400" },
  occupationalTherapy: { primary: "#C01610", hover: "#a01210" },
  psychology: { primary: "#C01610", hover: "#a01210" },
};

// Default blue theme (used when no service is selected yet)
const defaultThemePrimary = { primary: "#006DFF", hover: "#0059e6" };

type ThemeColor = { primary: string; hover: string };

const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface PersonalDetailsData {
  firstName: string;
  lastName: string;
  gender: "male" | "female" | "other" | null;
  dateOfBirth: string;
  email: string;
  phone: string;
  notes: string;
  emailVerified?: boolean;
}

export interface PersonalDetailsProps {
  defaultData?: Partial<PersonalDetailsData>;
  onDataChange?: (data: PersonalDetailsData) => void;
  bookingSummary?: {
    sessionType: "online" | "in-person";
    supportOption: string;
    duration: string;
    price: number;
    appointmentDate?: Date | null;
    appointmentTime?: string | null;
    practitionerName?: string | null;
    practitionerRole?: string | null;
    practitionerImage?: string | null;
  };
  requireEmailVerification?: boolean;
  serviceTheme?: ServiceTheme;
}

// Form Label Component
const FormLabel = ({
  label,
  required = false,
  hint,
}: {
  label: string;
  required?: boolean;
  hint?: string;
}) => (
  <div className="flex flex-wrap items-center gap-2.5">
    <div className="flex items-center gap-0.5">
      <span className="font-onest text-sm font-medium text-neutral-800">
        {label}
      </span>
      {required && (
        <span className="font-medium text-base text-red-600">*</span>
      )}
    </div>
    {hint && (
      <span className="font-onest text-sm font-light text-neutral-800/40">
        {hint}
      </span>
    )}
  </div>
);

// Form Input Component
const FormInput = ({
  placeholder,
  value,
  onChange,
  type = "text",
  icon,
  themeColor,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  icon?: React.ReactNode;
  themeColor: ThemeColor;
}) => (
  <div
    className="flex items-center justify-between overflow-hidden rounded-[10px] border px-[17px] py-[11px]"
    style={{ borderColor: hexToRgba(themeColor.primary, 0.3) }}
  >
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent font-onest text-sm font-normal text-neutral-800 placeholder:text-neutral-500/50 focus:outline-none"
    />
    {icon && <div className="ml-2 shrink-0">{icon}</div>}
  </div>
);

// Gender Option Button
const GenderOption = ({
  label,
  selected,
  onClick,
  themeColor,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  themeColor: ThemeColor;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      flex-1 overflow-hidden rounded-[10px] border px-[17px] py-[11px]
      text-center font-onest text-sm transition-all
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
      ${selected ? "font-medium text-white" : "font-normal text-neutral-500/50"}
    `}
    style={
      {
        borderColor: selected ? themeColor.primary : hexToRgba(themeColor.primary, 0.3),
        backgroundColor: selected ? themeColor.primary : "transparent",
        "--tw-ring-color": themeColor.primary,
      } as CSSProperties
    }
    onMouseEnter={(e) => {
      if (!selected) e.currentTarget.style.borderColor = hexToRgba(themeColor.primary, 0.5);
    }}
    onMouseLeave={(e) => {
      if (!selected) e.currentTarget.style.borderColor = hexToRgba(themeColor.primary, 0.3);
    }}
  >
    {label}
  </button>
);

// Extended Booking Summary for Personal Details
const ExtendedBookingSummary = ({
  sessionType,
  supportOption,
  duration,
  price,
  appointmentDate,
  appointmentTime,
  practitionerName,
  practitionerRole,
  practitionerImage,
  themeColor,
}: NonNullable<PersonalDetailsProps["bookingSummary"]> & { themeColor: ThemeColor }) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    const existingAmpm = time.match(/\s*(AM|PM)$/i);
    if (existingAmpm) {
      const cleaned = time.replace(/\s*(AM|PM)$/i, "").trim();
      const [hours, minutes] = cleaned.split(":");
      const hour = parseInt(hours);
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${existingAmpm[1].toUpperCase()}`;
    }
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const SessionIcon = sessionType === "online" ? Video : Armchair;

  return (
    <div className="flex flex-col">
      <h3 className="p-2.5 font-onest text-2xl font-semibold text-black">
        Booking Summary
      </h3>

      {/* Session Type Badge */}
      <div className="p-2.5">
        <div
          className="inline-flex items-center gap-2.5 rounded-[20px] border-4 border-white px-5 py-2.5"
          style={{
            backgroundColor: themeColor.primary,
            boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          }}
        >
          <SessionIcon className="size-6 text-white" />
          <span className="font-onest font-medium text-white">
            {sessionType === "online" ? "Online Session" : "In-Person Session"}
          </span>
        </div>
      </div>

      {/* Practitioner Info */}
      {practitionerName && (
        <div className="p-2.5">
          <div className="flex items-center gap-2.5">
            <div className="relative size-[60px] overflow-hidden rounded-full">
              <Image
                src={practitionerImage || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg"}
                alt={practitionerName}
                fill
                className="object-cover"
                sizes="60px"
              />
            </div>
            <div className="flex flex-col gap-2.5 p-2.5">
              <span className="font-onest text-xl font-semibold text-black">
                {practitionerName}
              </span>
              {practitionerRole && (
                <span className="font-onest text-base font-normal text-black">
                  {practitionerRole}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Option */}
      <div className="p-2.5">
        <div className="border-l-2 pl-2.5" style={{ borderColor: themeColor.primary }}>
          <div className="p-2.5">
            <span className="font-onest text-base font-semibold text-black">
              {supportOption}
            </span>
          </div>
          <div className="flex items-center gap-7 p-2.5">
            <div className="flex items-center gap-1.5">
              <Clock className="size-6" style={{ color: themeColor.primary }} />
              <span className="font-onest text-xl font-medium" style={{ color: themeColor.primary }}>
                {duration}
              </span>
            </div>
            <span className="font-onest text-xl font-bold text-black/60">
              ${price}
            </span>
          </div>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="rounded-[10px] p-2.5" style={{ backgroundColor: hexToRgba(themeColor.primary, 0.1) }}>
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Appointment
          </span>
        </div>
        <div className="flex gap-2.5 px-2.5">
          <span className="font-onest text-base font-normal text-black/60">
            {appointmentDate ? formatDate(appointmentDate) : "Select a date"}
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {appointmentTime ? formatTime(appointmentTime) : "Select a time"}
          </span>
        </div>
        <div className="px-2.5 pb-2.5">
          <span className="font-onest text-xs font-normal text-black/50">
            {getBusinessTimezoneDisplay(appointmentDate)}
          </span>
        </div>
      </div>

      {/* Total Cost */}
      <div className="border-b border-black/20 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Total Cost
          </span>
        </div>
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Session Fee
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${price} AUD
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="p-2.5">
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Total
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${price} AUD
          </span>
        </div>
      </div>
    </div>
  );
};

// OTP Input Component
const OtpInput = ({
  value,
  onChange,
  onVerify,
  onResend,
  isVerifying,
  error,
  themeColor,
  timeRemaining,
  resendCooldown,
  isExpired,
}: {
  value: string;
  onChange: (value: string) => void;
  onVerify: () => void;
  onResend: () => void;
  isVerifying: boolean;
  error?: string | null;
  themeColor: ThemeColor;
  timeRemaining: number;
  resendCooldown: number;
  isExpired: boolean;
}) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="mt-2 flex flex-col gap-2 rounded-[10px] border p-4"
      style={{ borderColor: hexToRgba(themeColor.primary, 0.3) }}
    >
      <div className="flex items-center justify-between">
        <span className="font-onest text-sm font-medium text-neutral-800">
          Enter the verification code sent to your email
        </span>
        {!isExpired && (
          <span
            className={`font-onest text-sm font-medium ${
              timeRemaining <= 60 ? "text-red-500" : "text-neutral-500"
            }`}
          >
            {formatTime(timeRemaining)}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          maxLength={6}
          disabled={isExpired}
          className="flex-1 rounded-[10px] border px-4 py-2 font-onest text-base font-medium tracking-widest focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ borderColor: hexToRgba(themeColor.primary, 0.3) }}
          onFocus={(e) => {
            if (!isExpired) e.currentTarget.style.borderColor = themeColor.primary;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = hexToRgba(themeColor.primary, 0.3);
          }}
        />
        <button
          type="button"
          onClick={onVerify}
          disabled={value.length !== 6 || isVerifying || isExpired}
          className="rounded-[10px] px-4 py-2 font-onest text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: themeColor.primary }}
          onMouseEnter={(e) => {
            if (value.length === 6 && !isVerifying && !isExpired)
              e.currentTarget.style.backgroundColor = themeColor.hover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = themeColor.primary;
          }}
        >
          {isVerifying ? <Loader2 className="size-5 animate-spin" /> : "Verify"}
        </button>
      </div>
      {error && <span className="font-onest text-sm text-red-500">{error}</span>}
      {isExpired && (
        <span className="font-onest text-sm text-red-500">
          Code expired. Please request a new one.
        </span>
      )}
      <button
        type="button"
        onClick={onResend}
        disabled={resendCooldown > 0}
        className="text-left font-onest text-sm font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
        style={{ color: themeColor.primary }}
      >
        {resendCooldown > 0
          ? `Resend code in ${formatTime(resendCooldown)}`
          : isExpired
            ? "Send new code"
            : "Resend code"}
      </button>
    </div>
  );
};

// Main Component
const PersonalDetails = ({
  defaultData,
  onDataChange,
  bookingSummary,
  requireEmailVerification = true,
  serviceTheme,
}: PersonalDetailsProps) => {
  const themeColor = serviceTheme ? themePrimaryColors[serviceTheme] : defaultThemePrimary;
  const [formData, setFormData] = useState<PersonalDetailsData>({
    firstName: defaultData?.firstName || "",
    lastName: defaultData?.lastName || "",
    gender: defaultData?.gender || null,
    dateOfBirth: defaultData?.dateOfBirth || "",
    email: defaultData?.email || "",
    phone: defaultData?.phone || "",
    notes: defaultData?.notes || "",
    emailVerified: defaultData?.emailVerified || false,
  });

  // OTP state
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState(defaultData?.email || "");
  
  // OTP timer state (5 minutes = 300 seconds)
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(300);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // OTP mutations
  const [sendOtp, { isLoading: isSendingOtp }] = useSenOTPMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOTPMutation();

  // OTP countdown timer effect
  useEffect(() => {
    if (!showOtpInput || otpExpired) return;

    const timer = setInterval(() => {
      setOtpTimeRemaining((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          setOtpError("Verification code has expired. Please request a new one.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showOtpInput, otpExpired]);

  // Resend cooldown timer effect
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const updateField = <K extends keyof PersonalDetailsData>(
    field: K,
    value: PersonalDetailsData[K]
  ) => {
    const newData = { ...formData, [field]: value };
    // Reset email verification if email changes
    if (field === "email" && value !== verifiedEmail) {
      newData.emailVerified = false;
      setShowOtpInput(false);
      setOtpValue("");
      setOtpError(null);
      setOtpTimeRemaining(300);
      setOtpExpired(false);
      setResendCooldown(0);
    }
    setFormData(newData);
    onDataChange?.(newData);
  };

  const handleSendOtp = async () => {
    if (!formData.email || resendCooldown > 0) return;
    setOtpError(null);
    try {
      await sendOtp({ email: formData.email }).unwrap();
      setShowOtpInput(true);
      setOtpTimeRemaining(300); // Reset to 5 minutes
      setOtpExpired(false);
      setOtpValue(""); // Clear any previous OTP value
      setResendCooldown(120); // 120 second (2 minute) cooldown before next resend
    } catch (error) {
      console.error("Failed to send OTP:", error);
      setOtpError("Failed to send verification code. Please try again.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.email || otpValue.length !== 6 || otpExpired) return;
    setOtpError(null);
    try {
      await verifyOtp({ email: formData.email, otp: otpValue }).unwrap();
      setVerifiedEmail(formData.email);
      const newData = { ...formData, emailVerified: true };
      setFormData(newData);
      onDataChange?.(newData);
      setShowOtpInput(false);
      setOtpValue("");
      setOtpTimeRemaining(300);
      setOtpExpired(false);
      setResendCooldown(0);
    } catch (error) {
      console.error("Failed to verify OTP:", error);
      setOtpError("Invalid code. Please check and try again.");
    }
  };

  const isEmailVerified = formData.emailVerified && formData.email === verifiedEmail;

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      {/* Header */}
      <div className="flex flex-col">
        <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          Personal Details
        </h2>
        <p className="max-w-[545px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          Please provide your information to create your appointment. All
          details are kept confidential.
        </p>
      </div>

      {/* Main Content */}
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-[30px]">
        {/* Form Section */}
        <div className="flex flex-1 flex-col gap-6 md:gap-[30px]">
          {/* Basic Information */}
          <div className="flex flex-col gap-2.5">
            <div className="p-2.5">
              <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
                Basic Information
              </h3>
            </div>

            <div className="flex flex-col">
              {/* First Name & Last Name */}
              <div className="flex flex-col sm:flex-row">
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="First Name" required />
                  <FormInput
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(value) => updateField("firstName", value)}
                    themeColor={themeColor}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="Last Name" required />
                  <FormInput
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(value) => updateField("lastName", value)}
                    themeColor={themeColor}
                  />
                </div>
              </div>

              {/* Gender & Date of Birth */}
              <div className="flex flex-col sm:flex-row">
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="Gender" required />
                  <div className="flex gap-2">
                    <GenderOption
                      label="Male"
                      selected={formData.gender === "male"}
                      onClick={() => updateField("gender", "male")}
                      themeColor={themeColor}
                    />
                    <GenderOption
                      label="Female"
                      selected={formData.gender === "female"}
                      onClick={() => updateField("gender", "female")}
                      themeColor={themeColor}
                    />
                    <GenderOption
                      label="Others"
                      selected={formData.gender === "other"}
                      onClick={() => updateField("gender", "other")}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="Date of Birth" required />
                  <FormInput
                    placeholder="Pick your date of birth"
                    value={formData.dateOfBirth}
                    onChange={(value) => updateField("dateOfBirth", value)}
                    type="date"
                    themeColor={themeColor}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="flex flex-col gap-2.5">
            <div className="p-2.5">
              <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
                Contact Information
              </h3>
            </div>

            <div className="flex flex-col">
              {/* Email & Phone */}
              <div className="flex flex-col sm:flex-row">
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="Email Address" required />
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <FormInput
                        placeholder="demo@email.com"
                        value={formData.email}
                        onChange={(value) => updateField("email", value)}
                        type="email"
                        icon={
                          isEmailVerified ? (
                            <CheckCircle className="size-5 text-green-500" />
                          ) : undefined
                        }
                        themeColor={themeColor}
                      />
                    </div>
                    {requireEmailVerification && !isEmailVerified && formData.email && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || !formData.email}
                        className="shrink-0 rounded-[10px] border px-4 py-2 font-onest text-sm font-medium transition-colors disabled:opacity-50"
                        style={{ borderColor: themeColor.primary, color: themeColor.primary }}
                        onMouseEnter={(e) => {
                          if (!isSendingOtp) e.currentTarget.style.backgroundColor = hexToRgba(themeColor.primary, 0.05);
                        }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        {isSendingOtp ? (
                          <Loader2 className="size-5 animate-spin" />
                        ) : (
                          "Verify"
                        )}
                      </button>
                    )}
                  </div>
                  {isEmailVerified && (
                    <span className="font-onest text-sm text-green-600">
                      Email verified
                    </span>
                  )}
                  {showOtpInput && !isEmailVerified && (
                    <OtpInput
                      value={otpValue}
                      onChange={setOtpValue}
                      onVerify={handleVerifyOtp}
                      onResend={handleSendOtp}
                      isVerifying={isVerifyingOtp}
                      error={otpError}
                      themeColor={themeColor}
                      timeRemaining={otpTimeRemaining}
                      resendCooldown={resendCooldown}
                      isExpired={otpExpired}
                    />
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel
                    label="Phone Number"
                    required
                    hint="(We will use this number to contact you)"
                  />
                  <FormInput
                    placeholder="+61 400 000 000"
                    value={formData.phone}
                    onChange={(value) => updateField("phone", value)}
                    type="tel"
                    themeColor={themeColor}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="flex flex-col gap-2.5">
            <div className="p-2.5">
              <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
                Additional Information
              </h3>
            </div>

            <div className="flex flex-col">
              <div className="flex">
                <div className="flex flex-1 flex-col gap-2 p-2.5">
                  <FormLabel label="Appointment Notes (Optional)" />
                  <textarea
                    value={formData.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="You can share your specific concerns here"
                    rows={5}
                    className="w-full resize-none overflow-hidden rounded-[10px] border bg-transparent px-[17px] py-[11px] font-onest text-sm font-normal text-neutral-800 placeholder:text-neutral-500/50 focus:outline-none focus:ring-2"
                    style={
                      {
                        borderColor: hexToRgba(themeColor.primary, 0.3),
                        "--tw-ring-color": hexToRgba(themeColor.primary, 0.3),
                      } as CSSProperties
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Summary */}
        {bookingSummary && (
          <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-[493px] lg:shrink-0">
            <ExtendedBookingSummary {...bookingSummary} themeColor={themeColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalDetails;
